import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Phone } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Reveal";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/brand";

const ROCK_HILL = [34.9249, -81.0251];
const AREAS = ["Rock Hill", "Fort Mill", "Tega Cay", "York", "Clover", "Lake Wylie", "I-77 Corridor"];

export const ServiceMap = () => {
  const mapEl = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: ROCK_HILL,
      zoom: 10,
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });
    mapRef.current = map;

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      {
        attribution: '&copy; OpenStreetMap &copy; CARTO',
        maxZoom: 19,
        subdomains: "abcd",
      }
    ).addTo(map);

    L.circle(ROCK_HILL, {
      radius: 32000,
      color: "#39FF14",
      weight: 2,
      fillColor: "#39FF14",
      fillOpacity: 0.08,
    }).addTo(map);

    const icon = L.divIcon({
      className: "",
      html: `<div style="width:20px;height:20px;border-radius:50%;background:#39FF14;box-shadow:0 0 0 6px rgba(57,255,20,0.25),0 0 18px rgba(57,255,20,0.8);"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    L.marker(ROCK_HILL, { icon })
      .addTo(map)
      .bindPopup("<b>Twizted Tow</b><br/>Rock Hill, SC");

    setTimeout(() => map.invalidateSize(), 300);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <section
      id="coverage"
      data-testid="service-map-section"
      className="relative z-10 bg-[var(--tt-surface)] px-5 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
        <Reveal>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--tt-green)] mb-5">
              / Coverage Area
            </p>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter mb-6">
              We reach you <br />
              <span className="text-zinc-500">across York County.</span>
            </h2>
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-md mb-8">
              Based in Rock Hill and covering a ~25-mile radius around the I-77 corridor. If you're on
              the map, a flatbed is already close.
            </p>
            <div className="flex flex-wrap gap-3 mb-9">
              {AREAS.map((a) => (
                <span
                  key={a}
                  data-testid={`coverage-area-${a.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300 border border-white/15 px-3 py-2 hover:border-[var(--tt-green)] hover:text-[var(--tt-green)] transition-colors"
                >
                  <MapPin weight="fill" className="h-3.5 w-3.5 text-[var(--tt-green)]" />
                  {a}
                </span>
              ))}
            </div>
            <a
              href={PHONE_TEL}
              data-testid="coverage-call-button"
              className="inline-flex items-center gap-3 font-display uppercase text-lg text-white border-b-2 border-[var(--tt-green)] pb-1 hover:text-[var(--tt-green)] transition-colors"
            >
              <Phone weight="fill" className="h-5 w-5" />
              Not sure if we cover you? Call {PHONE_DISPLAY}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="relative border border-white/10 overflow-hidden tt-clip-card">
            <div
              ref={mapEl}
              data-testid="coverage-map"
              className="h-[340px] md:h-[460px] w-full z-0"
              style={{ background: "#0a0a0a" }}
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[var(--tt-green)]/10" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
